import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkObserveContent, ObserversModule } from '@angular/cdk/observers';
import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, booleanAttribute, Input, Output, ContentChildren, ContentChild, Optional, Self, ChangeDetectionStrategy, Component, ViewChild, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NxErrorComponent, NxLabelComponent, NxErrorModule, NxLabelModule } from '@aposin/ng-aquila/base';
import { NxAbstractControl } from '@aposin/ng-aquila/shared';
import * as i2 from '@aposin/ng-aquila/utils';
import { randomString } from '@aposin/ng-aquila/utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as i3 from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { NxIconModule } from '@aposin/ng-aquila/icon';

/** The change event object emitted by the radio group and radio button. */
const _c0 = [[["nx-label"]], "*", [["nx-error"]]];
const _c1 = ["nx-label", "*", "nx-error"];
function NxRadioGroupComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0, 2);
  }
}
const _c2 = ["radioLabelWrapper"];
const _c3 = ["input"];
const _c4 = ["*"];
function NxRadioComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 5);
  }
}
class NxRadioChange {
  /** The NxRadioComponent that emits the change event. NxRadioChange object can be instantiated with source radio component and a value. */
  constructor(source, value) {
    this.source = source;
    this.value = value;
  }
}
let nextId = 0;
class NxRadioGroupComponent {
  /** Sets all radios in the group to readonly. */
  set readonly(value) {
    this._readonly = value;
    this._stateChanges.next();
  }
  get readonly() {
    return this._readonly;
  }
  /** Sets the Id of the radio group. */
  set id(value) {
    if (this._id !== value) {
      this._id = value;
      this._cdr.markForCheck();
    }
  }
  get id() {
    return this._id;
  }
  /** Whether every radio button in this group should be disabled. */
  set disabled(value) {
    this._disabled = coerceBooleanProperty(value);
    // inform childs about the change where CD should be triggered
    this._stateChanges.next();
  }
  get disabled() {
    return this._disabled;
  }
  /** Whether the radio group should have negative styling. */
  set negative(value) {
    this._negative = coerceBooleanProperty(value);
    this._cdr.markForCheck();
  }
  get negative() {
    return this._negative;
  }
  /** Sets if at least an option should be selected. */
  set required(value) {
    this._required = coerceBooleanProperty(value);
    this._stateChanges.next();
  }
  get required() {
    return this._required;
  }
  // this is also the name attribute, which is mandatory in conjunction with ngModel, hence no nx prefix
  /** Sets the name of this radio group, which is mandatory in conjunction with ngModel (Default: null). */
  set name(value) {
    this._name = value;
    this._stateChanges.next();
  }
  get name() {
    return this._name;
  }
  /** Sets the value of the selected radion button in this group (Default: null). */
  set value(newValue) {
    if (this._value !== newValue) {
      // Set this before proceeding to ensure no circular loop occurs with selection.
      this._value = newValue;
      this._updateSelectedRadioFromValue();
      this._checkSelectedRadioButton();
    }
  }
  get value() {
    return this._value;
  }
  constructor(_cdr, ngControl, _parentForm, _parentFormGroup, _errorStateMatcher) {
    this._cdr = _cdr;
    this.ngControl = ngControl;
    this._parentForm = _parentForm;
    this._parentFormGroup = _parentFormGroup;
    this._errorStateMatcher = _errorStateMatcher;
    /** @docs-private */
    this.errorState = false;
    this._readonly = false;
    // emits when the internal state changes on properties which are relevant
    // for the radio buttons so that they can mark themself for check
    this._stateChanges = new Subject();
    this._id = `nx-radio-group-${nextId++}`;
    this._disabled = false;
    this._negative = false;
    this._required = false;
    /** An event is dispatched on each group value change. */
    this.groupValueChange = new EventEmitter();
    // The currently selected radio button; should match _value
    this._selected = null;
    this._name = `nx-radio-group-${nextId++}`;
    this._value = null;
    this._onChange = () => {};
    this._onTouched = () => {};
    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }
  ngAfterContentInit() {
    if (this.ngControl) {
      // prevent group overwrite radio buttons since there is no value from group
      this._updateSelectedRadioFromValue();
    }
    this._checkSelectedRadioButton();
    const errorIds = this.errorChildren.map(errorItem => errorItem.id).join(' ');
    this._radios.forEach(radioButton => radioButton.ariaDescribedBy = errorIds);
  }
  ngDoCheck() {
    if (this.ngControl) {
      // We need to re-evaluate this on every change detection cycle, because there are some
      // error triggers that we can't subscribe to (e.g. parent form submissions). This means
      // that whatever logic is in here has to be super lean or we risk destroying the performance.
      this.updateErrorState();
    }
  }
  ngOnDestroy() {
    this._stateChanges.complete();
  }
  writeValue(value) {
    this.value = value;
  }
  registerOnChange(fn) {
    this._onChange = fn;
  }
  registerOnTouched(fn) {
    this._onTouched = fn;
  }
  /** @docs-private this is meant to be called by the radio buttons in this group */
  change(value) {
    this.value = value;
    this._onChange(value);
    this.groupValueChange.emit(new NxRadioChange(this._selected, this._value));
  }
  /** @docs-private this is meant to be called by the radio buttons in this group. */
  touch() {
    if (this._onTouched) {
      this._onTouched();
    }
  }
  setDisabledState(isDisabled) {
    this.disabled = isDisabled;
  }
  setReadonly(value) {
    this.readonly = value;
    this._cdr.markForCheck();
  }
  _updateSelectedRadioFromValue() {
    // If the value already matches the selected radio, do nothing.
    const isAlreadySelected = this._selected != null && this._selected.value === this._value;
    if (this._radios != null && !isAlreadySelected) {
      this._selected = null;
      this._radios.forEach(radio => {
        radio.checked = this.value === radio.value;
        if (radio.checked) {
          this._selected = radio;
        }
      });
    }
  }
  _checkSelectedRadioButton() {
    if (this._selected && !this._selected.checked) {
      this._selected.checked = true;
    }
  }
  /** @docs-private */
  updateErrorState() {
    const oldState = this.errorState;
    const parent = this._parentFormGroup || this._parentForm;
    const control = this.ngControl ? this.ngControl.control : null;
    const newState = this._errorStateMatcher.isErrorState(control, parent);
    if (newState !== oldState) {
      this.errorState = newState;
      this._cdr.markForCheck();
    }
  }
  static {
    this.ɵfac = function NxRadioGroupComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxRadioGroupComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.NgControl, 10), i0.ɵɵdirectiveInject(i1.NgForm, 8), i0.ɵɵdirectiveInject(i1.FormGroupDirective, 8), i0.ɵɵdirectiveInject(i2.ErrorStateMatcher));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxRadioGroupComponent,
      selectors: [["nx-radio-group"]],
      contentQueries: function NxRadioGroupComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NxLabelComponent, 5);
          i0.ɵɵcontentQuery(dirIndex, NxErrorComponent, 4);
          i0.ɵɵcontentQuery(dirIndex, NxRadioComponent, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._label = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.errorChildren = _t);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._radios = _t);
        }
      },
      hostAttrs: ["role", "radiogroup"],
      hostVars: 6,
      hostBindings: function NxRadioGroupComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("id", ctx.id)("required", ctx.required)("aria-labelledby", (ctx._label == null ? null : ctx._label.id) || null)("aria-nx-radio-group", ctx.ariaDescribedBy);
          i0.ɵɵclassProp("nx-radio-group--negative", ctx.negative);
        }
      },
      inputs: {
        readonly: [2, "readonly", "readonly", booleanAttribute],
        id: "id",
        disabled: "disabled",
        negative: "negative",
        required: "required",
        name: "name",
        value: "value"
      },
      outputs: {
        groupValueChange: "groupValueChange"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NxAbstractControl,
        useExisting: forwardRef(() => NxRadioGroupComponent)
      }])],
      ngContentSelectors: _c1,
      decls: 3,
      vars: 1,
      template: function NxRadioGroupComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c0);
          i0.ɵɵprojection(0);
          i0.ɵɵprojection(1, 1);
          i0.ɵɵtemplate(2, NxRadioGroupComponent_Conditional_2_Template, 1, 0);
        }
        if (rf & 2) {
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.errorState ? 2 : -1);
        }
      },
      styles: ["[_nghost-%COMP%]     nx-error{margin-bottom:8px}[_nghost-%COMP%]     nx-label{margin-bottom:16px;display:block}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxRadioGroupComponent, [{
    type: Component,
    args: [{
      selector: 'nx-radio-group',
      changeDetection: ChangeDetectionStrategy.OnPush,
      host: {
        role: 'radiogroup',
        '[attr.id]': 'id',
        '[attr.required]': 'required',
        '[class.nx-radio-group--negative]': 'negative',
        '[attr.aria-labelledby]': 'this._label?.id  || null',
        '[attr.aria-nx-radio-group]': 'ariaDescribedBy'
      },
      imports: [],
      providers: [{
        provide: NxAbstractControl,
        useExisting: forwardRef(() => NxRadioGroupComponent)
      }],
      template: "<ng-content select=\"nx-label\"></ng-content>\n<ng-content></ng-content>\n@if (errorState) {\n<ng-content select=\"nx-error\"></ng-content>\n}\n",
      styles: [":host ::ng-deep nx-error{margin-bottom:8px}:host ::ng-deep nx-label{margin-bottom:16px;display:block}\n"]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }, {
    type: i1.NgControl,
    decorators: [{
      type: Optional
    }, {
      type: Self
    }]
  }, {
    type: i1.NgForm,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i1.FormGroupDirective,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i2.ErrorStateMatcher
  }], {
    _label: [{
      type: ContentChild,
      args: [forwardRef(() => NxLabelComponent)]
    }],
    errorChildren: [{
      type: ContentChildren,
      args: [NxErrorComponent]
    }],
    _radios: [{
      type: ContentChildren,
      args: [forwardRef(() => NxRadioComponent), {
        descendants: true
      }]
    }],
    readonly: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    id: [{
      type: Input
    }],
    disabled: [{
      type: Input
    }],
    negative: [{
      type: Input
    }],
    required: [{
      type: Input
    }],
    groupValueChange: [{
      type: Output
    }],
    name: [{
      type: Input
    }],
    value: [{
      type: Input
    }]
  });
})();
class NxRadioComponent {
  /** Sets radio to readonly. */
  set readonly(value) {
    this._readonly = value;
  }
  get readonly() {
    return this.radioGroup?.readonly || this._readonly;
  }
  setReadonly(value) {
    this.readonly = value;
    this._cdr.markForCheck();
  }
  /** Sets the id of the radio component. */
  set id(value) {
    if (this._id !== value) {
      this._id = value;
      this._cdr.markForCheck();
    }
  }
  get id() {
    return this._id;
  }
  /** @docs-private */
  get inputId() {
    return `${this.id}-input`;
  }
  /** @docs-private */
  get labelId() {
    return `${this.id}-label`;
  }
  // this is also the name attribute, which is mandatory in conjunction with ngModel, hence no nx prefix
  /** Sets the name of this radio component, which is mandatory in conjunction with ngModel (Default: null). */
  set name(value) {
    if (this._name !== value) {
      this._name = value;
      this._cdr.markForCheck();
    }
  }
  get name() {
    return this.radioGroup?.name || this._name;
  }
  /**
   * Sets the label size of the radio button.
   * The default value is `big`.
   */
  set labelSize(value) {
    if (this._labelSize !== value) {
      this._labelSize = value;
      this._cdr.markForCheck();
    }
  }
  get labelSize() {
    return this._labelSize;
  }
  /**
   * Whether the negative set of styles should be used.
   *
   * If the radio button is placed in a radio group, the negative value
   * is overwritten by the group's negative value.
   */
  set negative(value) {
    const newValue = coerceBooleanProperty(value);
    if (this._negative !== newValue) {
      this._negative = newValue;
      this._cdr.markForCheck();
    }
  }
  get negative() {
    return this.radioGroup ? this.radioGroup.negative : this._negative;
  }
  /** @docs-private */
  get labelHasContent() {
    return !!this._radioLabelWrapper.nativeElement.innerHTML.trim();
  }
  /** Sets the value of the form control element (Default: null). */
  set value(value) {
    if (value !== this._value) {
      this._value = value;
      this.onChangeCallback(value);
    }
  }
  get value() {
    return this._value;
  }
  /** Whether the radio component is selected. */
  set checked(value) {
    if (this._checked !== value) {
      this._checked = value;
      this._cdr.markForCheck();
    }
  }
  /** @docs-private */
  get checked() {
    return this._checked;
  }
  /** Whether the radio button should be disabled or not. */
  set disabled(value) {
    this._disabled = coerceBooleanProperty(value);
    this._cdr.markForCheck();
  }
  /** @docs-private */
  get disabled() {
    return this._disabled || !!this.radioGroup?.disabled;
  }
  /** Sets if at least a radio button should be selected. */
  set required(value) {
    this._required = value;
    // needed when the outer component is set to onPush because it is a native property
    // on the host element
    this._cdr.markForCheck();
  }
  /** @docs-private */
  get required() {
    return this._required || !!this.radioGroup?.required;
  }
  constructor(radioGroup, _cdr, _focusMonitor) {
    this.radioGroup = radioGroup;
    this._cdr = _cdr;
    this._focusMonitor = _focusMonitor;
    this.ariaLabel = null;
    this.ariaLabelledBy = null;
    this.ariaDescribedBy = null;
    this._readonly = false;
    this._id = `nx-radio-${nextId++}-${randomString()}`;
    this._name = null;
    this._labelSize = 'big';
    this._negative = false;
    /** An event is dispatched on each value change. */
    this.valueChange = new EventEmitter();
    this._value = null;
    this._checked = false;
    this._disabled = false;
    this._required = false;
    this._destroyed = new Subject();
    this.onChangeCallback = () => {};
    this.onTouchedCallback = () => {};
  }
  ngOnInit() {
    if (this.radioGroup) {
      this.name = this.radioGroup.name;
      // when relevant properties of the parent like name and disabled change
      // we need to let change detection know that the template needs an update
      this.radioGroup._stateChanges.pipe(takeUntil(this._destroyed)).subscribe(() => {
        this._cdr.markForCheck();
      });
      if (this.radioGroup.value === this._value) {
        this._checked = true;
      }
    }
  }
  ngAfterViewInit() {
    this._focusMonitor.monitor(this._nativeInput);
  }
  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
    this._focusMonitor.stopMonitoring(this._nativeInput);
  }
  /**
   * Callback for when the content of the label has changed.
   * @docs-private
   */
  labelContentChanged() {
    this._cdr.detectChanges();
  }
  writeValue(value) {
    if (value === this._value) {
      this._checked = true;
      this._cdr.markForCheck();
    }
  }
  registerOnChange(onChange) {
    this.onChangeCallback = onChange;
  }
  registerOnTouched(onTouched) {
    this.onTouchedCallback = onTouched;
  }
  /** Focuses the radio button element. */
  focus(focusOrigin) {
    this._focusMonitor.focusVia(this._nativeInput, focusOrigin);
  }
  /** Forward focus from host to hidden input field */
  _forwardFocusToInput() {
    this._nativeInput.nativeElement.focus();
  }
  /** @docs-private */
  touch(event) {
    const target = event.relatedTarget;
    const targetGroupId = target?.parentElement?.parentElement?.id;
    if (targetGroupId === this.radioGroup?.id) {
      return;
    }
    if (this.radioGroup) {
      this.radioGroup.touch();
    }
  }
  _onInputChange(event) {
    event.stopPropagation();
    this._checked = true;
    this.valueChange.emit(new NxRadioChange(this, this._value));
    this.onChangeCallback(this.value);
    if (this.radioGroup && this.value !== this.radioGroup.value) {
      this.radioGroup.change(this.value);
    }
  }
  _onInputClick(event) {
    if (this.readonly) {
      event.preventDefault();
    }
    // make sure click event propagation on the visually hidden input
    // are stopped to prevent multiple events bubbling up.
    event.stopPropagation();
  }
  /** @docs-private */
  _controlInvalid() {
    const form = this.radioGroup && (this.radioGroup._parentFormGroup || this.radioGroup._parentForm);
    const control = this.radioGroup?.ngControl ? this.radioGroup.ngControl.control : null;
    if (this.radioGroup?._errorStateMatcher) {
      return this.radioGroup._errorStateMatcher.isErrorState(control, form);
    }
    return !!(control?.invalid && (control.touched || form?.submitted));
  }
  static {
    this.ɵfac = function NxRadioComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxRadioComponent)(i0.ɵɵdirectiveInject(NxRadioGroupComponent, 8), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i3.FocusMonitor));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxRadioComponent,
      selectors: [["nx-radio"]],
      viewQuery: function NxRadioComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c2, 7);
          i0.ɵɵviewQuery(_c3, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._radioLabelWrapper = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._nativeInput = _t.first);
        }
      },
      hostVars: 12,
      hostBindings: function NxRadioComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("focus", function NxRadioComponent_focus_HostBindingHandler() {
            return ctx._forwardFocusToInput();
          });
        }
        if (rf & 2) {
          i0.ɵɵattribute("required", ctx.required)("aria-invalid", ctx._controlInvalid() || null);
          i0.ɵɵclassProp("nx-radio-button--small-label", ctx.labelSize === "small")("nx-radio-button--big-label", ctx.labelSize === "big")("nx-radio--negative", ctx.negative)("has-error", ctx._controlInvalid() || null)("is-readonly", ctx.readonly || null);
        }
      },
      inputs: {
        ariaLabel: "ariaLabel",
        ariaLabelledBy: "ariaLabelledBy",
        ariaDescribedBy: "ariaDescribedBy",
        readonly: [2, "readonly", "readonly", booleanAttribute],
        id: "id",
        name: "name",
        labelSize: "labelSize",
        negative: "negative",
        value: "value",
        checked: "checked",
        disabled: "disabled",
        required: "required"
      },
      outputs: {
        valueChange: "valueChange"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => NxRadioComponent),
        multi: true
      }, {
        provide: NxAbstractControl,
        useExisting: forwardRef(() => NxRadioComponent)
      }])],
      ngContentSelectors: _c4,
      decls: 8,
      vars: 14,
      consts: [["input", ""], ["radioLabelWrapper", ""], ["type", "radio", 1, "nx-radio__input", 3, "blur", "change", "click", "id", "disabled", "checked"], ["tabindex", "-1", 1, "nx-radio__label", 3, "id"], [1, "nx-radio__circle"], [1, "nx-radio__dot"], [1, "nx-radio__label--text", 3, "cdkObserveContent"]],
      template: function NxRadioComponent_Template(rf, ctx) {
        if (rf & 1) {
          const _r1 = i0.ɵɵgetCurrentView();
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "input", 2, 0);
          i0.ɵɵlistener("blur", function NxRadioComponent_Template_input_blur_0_listener($event) {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx.touch($event));
          })("change", function NxRadioComponent_Template_input_change_0_listener($event) {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx._onInputChange($event));
          })("click", function NxRadioComponent_Template_input_click_0_listener($event) {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx._onInputClick($event));
          });
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(2, "label", 3)(3, "div", 4);
          i0.ɵɵtemplate(4, NxRadioComponent_Conditional_4_Template, 1, 0, "div", 5);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(5, "span", 6, 1);
          i0.ɵɵlistener("cdkObserveContent", function NxRadioComponent_Template_span_cdkObserveContent_5_listener() {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx.labelContentChanged());
          });
          i0.ɵɵprojection(7);
          i0.ɵɵelementEnd()();
        }
        if (rf & 2) {
          i0.ɵɵproperty("id", ctx.inputId)("disabled", ctx.disabled)("checked", ctx.checked);
          i0.ɵɵattribute("name", ctx.name)("aria-describedby", ctx.ariaDescribedBy)("aria-labelledby", ctx.ariaLabelledBy)("aria-label", ctx.ariaLabel)("aria-invalid", ctx._controlInvalid() || null)("aria-disabled", ctx.disabled || ctx.readonly || null);
          i0.ɵɵadvance(2);
          i0.ɵɵclassProp("has-label", ctx.labelHasContent);
          i0.ɵɵproperty("id", ctx.labelId);
          i0.ɵɵattribute("for", ctx.inputId);
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.checked ? 4 : -1);
        }
      },
      dependencies: [CdkObserveContent],
      styles: ["[_nghost-%COMP%]{display:block;position:relative}.nx-radio__input[_ngcontent-%COMP%]{opacity:0;position:absolute}.nx-radio-button--small-label[_nghost-%COMP%]   .nx-radio__label[_ngcontent-%COMP%]{font-size:var(--radio-button-small-label-font-size);line-height:var(--radio-button-small-label-line-height);font-weight:var(--radio-button-small-label-font-weight);letter-spacing:var(--radio-button-small-label-letter-spacing)}.nx-radio-button--small-label[_nghost-%COMP%]   .nx-radio__label.has-label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{margin-top:var(--radio-button-small-circle-margin-top)}.nx-radio-button--small-label[_nghost-%COMP%]   .nx-radio__label--text[_ngcontent-%COMP%]:not(:empty){margin-left:var(--radio-button-small-label-margin-left);margin-top:var(--radio-button-small-label-margin-top)}[dir=rtl]   .nx-radio-button--small-label[_nghost-%COMP%]   .nx-radio__label--text[_ngcontent-%COMP%]:not(:empty){margin-right:var(--radio-button-small-label-margin-left);margin-left:initial}.nx-radio-button--big-label[_nghost-%COMP%]   .nx-radio__label[_ngcontent-%COMP%]{font-size:var(--radio-button-large-label-font-size);line-height:var(--radio-button-large-label-line-height);font-weight:var(--radio-button-large-label-font-weight);letter-spacing:var(--radio-button-large-label-letter-spacing)}.nx-radio-button--big-label[_nghost-%COMP%]   .nx-radio__label.has-label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{margin-top:var(--radio-button-large-circle-margin-top)}.nx-radio-button--big-label[_nghost-%COMP%]   .nx-radio__label--text[_ngcontent-%COMP%]:not(:empty){margin-left:var(--radio-button-large-label-margin-left);margin-top:var(--radio-button-large-label-margin-top)}[dir=rtl]   .nx-radio-button--big-label[_nghost-%COMP%]   .nx-radio__label--text[_ngcontent-%COMP%]:not(:empty){margin-right:var(--radio-button-large-label-margin-left);margin-left:initial}.nx-radio--negative[_nghost-%COMP%]   .nx-radio__label--text[_ngcontent-%COMP%]{color:var(--negative)}.nx-radio--negative[_nghost-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--negative)}.nx-radio--negative[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--negative);background-color:transparent}.nx-radio--negative[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__dot[_ngcontent-%COMP%]{background:var(--negative)}.nx-radio--negative[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:disabled + .nx-radio__label[_ngcontent-%COMP%]{opacity:.4}.nx-radio__label[_ngcontent-%COMP%]{color:var(--radio-button-label-color);cursor:pointer;display:inline-flex;position:relative;vertical-align:middle}.nx-radio__circle[_ngcontent-%COMP%]{border-width:2px;border-style:solid;border-color:var(--radio-button-circle-color);border-radius:50%;width:24px;min-width:24px;height:24px;position:relative;display:flex;justify-content:center;align-items:center}.nx-radio__dot[_ngcontent-%COMP%]{width:var(--radio-button-dot-size);height:var(--radio-button-dot-size);background-color:var(--radio-button-dot-background-color);border-radius:50%;transition:background-color .2s ease}.nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--radio-button-selected-circle-color);background-color:var(--radio-button-selected-background-color)}.nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__dot[_ngcontent-%COMP%]{background-color:var(--radio-button-selected-dot-color)}.nx-radio__input[_ngcontent-%COMP%]:disabled + .nx-radio__label[_ngcontent-%COMP%]{cursor:not-allowed;color:var(--radio-button-disabled-text-color)}.nx-radio__input[_ngcontent-%COMP%]:disabled + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--radio-button-disabled-border-color)}.nx-radio__input[_ngcontent-%COMP%]:checked:disabled + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{background-color:var(--radio-button-disabled-selected-background-color)}.nx-radio__input[_ngcontent-%COMP%]:checked:disabled + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__dot[_ngcontent-%COMP%]{background-color:var(--radio-button-disabled-selected-dot-color)}.is-readonly[_nghost-%COMP%]   .nx-radio__label[_ngcontent-%COMP%]:hover{cursor:not-allowed}.is-readonly[_nghost-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--radio-button-readonly-border-color)}.is-readonly[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{background-color:var(--radio-button-readonly-background-color);border-color:var(--radio-button-readonly-border-color)}.is-readonly[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__dot[_ngcontent-%COMP%]{background-color:var(--radio-button-readonly-selected-dot-color)}.nx-radio__input.cdk-keyboard-focused[_ngcontent-%COMP%] + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{box-shadow:var(--focus-box-shadow)}@media screen and (forced-colors: active),(forced-colors: active){.nx-radio__input.cdk-keyboard-focused[_ngcontent-%COMP%] + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}[_nghost-%COMP%]:not(.nx-radio--negative):not(.has-error):not(.is-readonly)   .nx-radio__input[_ngcontent-%COMP%]:not(:disabled) + .nx-radio__label[_ngcontent-%COMP%]:hover   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--radio-button-hover-circle-color)}[_nghost-%COMP%]:not(.nx-radio--negative):not(.has-error):not(.is-readonly)   .nx-radio__input[_ngcontent-%COMP%]:not(:disabled):checked + .nx-radio__label[_ngcontent-%COMP%]:hover   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--radio-button-hover-circle-color);background-color:var(--radio-button-hover-circle-color)}[_nghost-%COMP%]:not(.nx-radio--negative):not(.has-error):not(.is-readonly)   .nx-radio__input[_ngcontent-%COMP%]:not(:disabled):checked + .nx-radio__label[_ngcontent-%COMP%]:hover   .nx-radio__dot[_ngcontent-%COMP%]{background-color:var(--radio-button-hover-dot-color)}[_nghost-%COMP%]:not(.nx-radio--negative):not(.has-error):not(.is-readonly)   .nx-radio__input[_ngcontent-%COMP%]:not(:disabled) + .nx-radio__label[_ngcontent-%COMP%]:active   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--radio-button-active-circle-color)}[_nghost-%COMP%]:not(.nx-radio--negative):not(.has-error):not(.is-readonly)   .nx-radio__input[_ngcontent-%COMP%]:not(:disabled):checked + .nx-radio__label[_ngcontent-%COMP%]:active   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--radio-button-active-circle-color);background-color:var(--radio-button-active-circle-color)}[_nghost-%COMP%]:not(.nx-radio--negative):not(.has-error):not(.is-readonly)   .nx-radio__input[_ngcontent-%COMP%]:not(:disabled):checked + .nx-radio__label[_ngcontent-%COMP%]:active   .nx-radio__dot[_ngcontent-%COMP%]{background-color:var(--radio-button-hover-dot-color)}.has-error[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%] + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--danger)}.has-error[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{background-color:var(--danger)}.has-error[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%] + .nx-radio__label[_ngcontent-%COMP%]:hover   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--radio-button-error-hover-circle-color)}.has-error[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%] + .nx-radio__label[_ngcontent-%COMP%]:active   .nx-radio__circle[_ngcontent-%COMP%]{border-color:var(--radio-button-error-active-circle-color)}.has-error[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]:hover   .nx-radio__circle[_ngcontent-%COMP%]{background-color:var(--radio-button-error-hover-circle-color);border-color:var(--radio-button-error-hover-circle-color)}.has-error[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]:active   .nx-radio__circle[_ngcontent-%COMP%]{background-color:var(--radio-button-error-active-circle-color);border-color:var(--radio-button-error-active-circle-color)}@media screen and (forced-colors: active){[_nghost-%COMP%]   .nx-radio__label[_ngcontent-%COMP%]{color:buttonText!important}[_nghost-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{forced-color-adjust:none;border-color:buttonText!important;background-color:buttonFace!important}[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{border-color:highlight!important}[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:checked + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__dot[_ngcontent-%COMP%]{background-color:highlight!important}[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:disabled + .nx-radio__label[_ngcontent-%COMP%]{color:GrayText!important;opacity:1}[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:disabled + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__circle[_ngcontent-%COMP%]{border-color:GrayText!important}[_nghost-%COMP%]   .nx-radio__input[_ngcontent-%COMP%]:checked:disabled + .nx-radio__label[_ngcontent-%COMP%]   .nx-radio__dot[_ngcontent-%COMP%]{background-color:GrayText!important}}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxRadioComponent, [{
    type: Component,
    args: [{
      selector: 'nx-radio',
      changeDetection: ChangeDetectionStrategy.OnPush,
      providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => NxRadioComponent),
        multi: true
      }, {
        provide: NxAbstractControl,
        useExisting: forwardRef(() => NxRadioComponent)
      }],
      host: {
        '[attr.required]': 'required',
        '[class.nx-radio-button--small-label]': 'labelSize === "small"',
        '[class.nx-radio-button--big-label]': 'labelSize === "big"',
        '[class.nx-radio--negative]': 'negative',
        '[class.has-error]': '_controlInvalid() || null',
        '[attr.aria-invalid]': '_controlInvalid() || null',
        '[class.is-readonly]': 'readonly || null',
        '(focus)': '_forwardFocusToInput()'
      },
      imports: [CdkObserveContent],
      template: "<input\n    #input\n    type=\"radio\"\n    [id]=\"inputId\"\n    [attr.name]=\"name\"\n    [disabled]=\"disabled\"\n    (blur)=\"touch($event)\"\n    (change)=\"_onInputChange($event)\"\n    (click)=\"_onInputClick($event)\"\n    [checked]=\"checked\"\n    [attr.aria-describedby]=\"ariaDescribedBy\"\n    [attr.aria-labelledby]=\"ariaLabelledBy\"\n    [attr.aria-label]=\"ariaLabel\"\n    [attr.aria-invalid]=\"_controlInvalid() || null\"\n    [attr.aria-disabled]=\"disabled || readonly || null\"\n    class=\"nx-radio__input\"\n/>\n<label [id]=\"labelId\" [attr.for]=\"inputId\" tabindex=\"-1\" class=\"nx-radio__label\" [class.has-label]=\"labelHasContent\">\n    <div class=\"nx-radio__circle\">\n        @if (checked) {\n        <div class=\"nx-radio__dot\"></div>\n        }\n    </div>\n    <span class=\"nx-radio__label--text\" #radioLabelWrapper (cdkObserveContent)=\"labelContentChanged()\">\n        <ng-content></ng-content>\n    </span>\n</label>\n",
      styles: [":host{display:block;position:relative}.nx-radio__input{opacity:0;position:absolute}:host(.nx-radio-button--small-label) .nx-radio__label{font-size:var(--radio-button-small-label-font-size);line-height:var(--radio-button-small-label-line-height);font-weight:var(--radio-button-small-label-font-weight);letter-spacing:var(--radio-button-small-label-letter-spacing)}:host(.nx-radio-button--small-label) .nx-radio__label.has-label .nx-radio__circle{margin-top:var(--radio-button-small-circle-margin-top)}:host(.nx-radio-button--small-label) .nx-radio__label--text:not(:empty){margin-left:var(--radio-button-small-label-margin-left);margin-top:var(--radio-button-small-label-margin-top)}[dir=rtl] :host(.nx-radio-button--small-label) .nx-radio__label--text:not(:empty){margin-right:var(--radio-button-small-label-margin-left);margin-left:initial}:host(.nx-radio-button--big-label) .nx-radio__label{font-size:var(--radio-button-large-label-font-size);line-height:var(--radio-button-large-label-line-height);font-weight:var(--radio-button-large-label-font-weight);letter-spacing:var(--radio-button-large-label-letter-spacing)}:host(.nx-radio-button--big-label) .nx-radio__label.has-label .nx-radio__circle{margin-top:var(--radio-button-large-circle-margin-top)}:host(.nx-radio-button--big-label) .nx-radio__label--text:not(:empty){margin-left:var(--radio-button-large-label-margin-left);margin-top:var(--radio-button-large-label-margin-top)}[dir=rtl] :host(.nx-radio-button--big-label) .nx-radio__label--text:not(:empty){margin-right:var(--radio-button-large-label-margin-left);margin-left:initial}:host.nx-radio--negative .nx-radio__label--text{color:var(--negative)}:host.nx-radio--negative .nx-radio__circle{border-color:var(--negative)}:host.nx-radio--negative .nx-radio__input:checked+.nx-radio__label .nx-radio__circle{border-color:var(--negative);background-color:transparent}:host.nx-radio--negative .nx-radio__input:checked+.nx-radio__label .nx-radio__dot{background:var(--negative)}:host.nx-radio--negative .nx-radio__input:disabled+.nx-radio__label{opacity:.4}.nx-radio__label{color:var(--radio-button-label-color);cursor:pointer;display:inline-flex;position:relative;vertical-align:middle}.nx-radio__circle{border-width:2px;border-style:solid;border-color:var(--radio-button-circle-color);border-radius:50%;width:24px;min-width:24px;height:24px;position:relative;display:flex;justify-content:center;align-items:center}.nx-radio__dot{width:var(--radio-button-dot-size);height:var(--radio-button-dot-size);background-color:var(--radio-button-dot-background-color);border-radius:50%;transition:background-color .2s ease}.nx-radio__input:checked+.nx-radio__label .nx-radio__circle{border-color:var(--radio-button-selected-circle-color);background-color:var(--radio-button-selected-background-color)}.nx-radio__input:checked+.nx-radio__label .nx-radio__dot{background-color:var(--radio-button-selected-dot-color)}.nx-radio__input:disabled+.nx-radio__label{cursor:not-allowed;color:var(--radio-button-disabled-text-color)}.nx-radio__input:disabled+.nx-radio__label .nx-radio__circle{border-color:var(--radio-button-disabled-border-color)}.nx-radio__input:checked:disabled+.nx-radio__label .nx-radio__circle{background-color:var(--radio-button-disabled-selected-background-color)}.nx-radio__input:checked:disabled+.nx-radio__label .nx-radio__dot{background-color:var(--radio-button-disabled-selected-dot-color)}:host(.is-readonly) .nx-radio__label:hover{cursor:not-allowed}:host(.is-readonly) .nx-radio__circle{border-color:var(--radio-button-readonly-border-color)}:host(.is-readonly) .nx-radio__input:checked+.nx-radio__label .nx-radio__circle{background-color:var(--radio-button-readonly-background-color);border-color:var(--radio-button-readonly-border-color)}:host(.is-readonly) .nx-radio__input:checked+.nx-radio__label .nx-radio__dot{background-color:var(--radio-button-readonly-selected-dot-color)}.nx-radio__input.cdk-keyboard-focused+.nx-radio__label .nx-radio__circle{box-shadow:var(--focus-box-shadow)}@media screen and (forced-colors: active),(forced-colors: active){.nx-radio__input.cdk-keyboard-focused+.nx-radio__label .nx-radio__circle{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}:host:not(.nx-radio--negative):not(.has-error):not(.is-readonly) .nx-radio__input:not(:disabled)+.nx-radio__label:hover .nx-radio__circle{border-color:var(--radio-button-hover-circle-color)}:host:not(.nx-radio--negative):not(.has-error):not(.is-readonly) .nx-radio__input:not(:disabled):checked+.nx-radio__label:hover .nx-radio__circle{border-color:var(--radio-button-hover-circle-color);background-color:var(--radio-button-hover-circle-color)}:host:not(.nx-radio--negative):not(.has-error):not(.is-readonly) .nx-radio__input:not(:disabled):checked+.nx-radio__label:hover .nx-radio__dot{background-color:var(--radio-button-hover-dot-color)}:host:not(.nx-radio--negative):not(.has-error):not(.is-readonly) .nx-radio__input:not(:disabled)+.nx-radio__label:active .nx-radio__circle{border-color:var(--radio-button-active-circle-color)}:host:not(.nx-radio--negative):not(.has-error):not(.is-readonly) .nx-radio__input:not(:disabled):checked+.nx-radio__label:active .nx-radio__circle{border-color:var(--radio-button-active-circle-color);background-color:var(--radio-button-active-circle-color)}:host:not(.nx-radio--negative):not(.has-error):not(.is-readonly) .nx-radio__input:not(:disabled):checked+.nx-radio__label:active .nx-radio__dot{background-color:var(--radio-button-hover-dot-color)}:host.has-error .nx-radio__input+.nx-radio__label .nx-radio__circle{border-color:var(--danger)}:host.has-error .nx-radio__input:checked+.nx-radio__label .nx-radio__circle{background-color:var(--danger)}:host.has-error .nx-radio__input+.nx-radio__label:hover .nx-radio__circle{border-color:var(--radio-button-error-hover-circle-color)}:host.has-error .nx-radio__input+.nx-radio__label:active .nx-radio__circle{border-color:var(--radio-button-error-active-circle-color)}:host.has-error .nx-radio__input:checked+.nx-radio__label:hover .nx-radio__circle{background-color:var(--radio-button-error-hover-circle-color);border-color:var(--radio-button-error-hover-circle-color)}:host.has-error .nx-radio__input:checked+.nx-radio__label:active .nx-radio__circle{background-color:var(--radio-button-error-active-circle-color);border-color:var(--radio-button-error-active-circle-color)}@media screen and (forced-colors: active){:host .nx-radio__label{color:buttonText!important}:host .nx-radio__circle{forced-color-adjust:none;border-color:buttonText!important;background-color:buttonFace!important}:host .nx-radio__input:checked+.nx-radio__label .nx-radio__circle{border-color:highlight!important}:host .nx-radio__input:checked+.nx-radio__label .nx-radio__dot{background-color:highlight!important}:host .nx-radio__input:disabled+.nx-radio__label{color:GrayText!important;opacity:1}:host .nx-radio__input:disabled+.nx-radio__label .nx-radio__circle{border-color:GrayText!important}:host .nx-radio__input:checked:disabled+.nx-radio__label .nx-radio__dot{background-color:GrayText!important}}\n"]
    }]
  }], () => [{
    type: NxRadioGroupComponent,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i0.ChangeDetectorRef
  }, {
    type: i3.FocusMonitor
  }], {
    _radioLabelWrapper: [{
      type: ViewChild,
      args: ['radioLabelWrapper', {
        static: true
      }]
    }],
    _nativeInput: [{
      type: ViewChild,
      args: ['input']
    }],
    ariaLabel: [{
      type: Input
    }],
    ariaLabelledBy: [{
      type: Input
    }],
    ariaDescribedBy: [{
      type: Input
    }],
    readonly: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    id: [{
      type: Input
    }],
    name: [{
      type: Input
    }],
    labelSize: [{
      type: Input
    }],
    negative: [{
      type: Input
    }],
    valueChange: [{
      type: Output
    }],
    value: [{
      type: Input
    }],
    checked: [{
      type: Input
    }],
    disabled: [{
      type: Input
    }],
    required: [{
      type: Input
    }]
  });
})();
class NxRadioModule {
  static {
    this.ɵfac = function NxRadioModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxRadioModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NxRadioModule,
      imports: [CommonModule, NxIconModule, ObserversModule, NxRadioComponent, NxRadioGroupComponent],
      exports: [NxRadioComponent, NxRadioGroupComponent, NxErrorModule, NxLabelModule]
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, NxIconModule, ObserversModule, NxErrorModule, NxLabelModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxRadioModule, [{
    type: NgModule,
    args: [{
      exports: [NxRadioComponent, NxRadioGroupComponent, NxErrorModule, NxLabelModule],
      imports: [CommonModule, NxIconModule, ObserversModule, NxRadioComponent, NxRadioGroupComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NxRadioChange, NxRadioComponent, NxRadioGroupComponent, NxRadioModule };
